import { useState } from 'react';
import { AxiosError } from 'axios';
import { toast } from 'sonner';
import { useDocumentCaptureStore } from '../store/documentCaptureStore';

/**
 * Shared submit path for every review decision (86d3rrfdk).
 *
 * One place owns the response contract so no tab reinvents it:
 * - **403** is handled by the global apiClient interceptor (it toasts "Access Denied"). We stay
 *   silent here so there's no double-toast, and just refetch the queue.
 * - **409** (epoch drift) → "the underlying data changed — refreshed" + refetch, so the reviewer
 *   sees the fresh state instead of acting on stale data.
 * - **400** (not-applicable) → surface the server's plain-language message.
 * - **success** → the caller's own success toast, then refetch + advance.
 *
 * The action itself (assign vs resolve-multi-match vs reject…) is the caller's `run` thunk — this
 * hook owns only the outcome handling, busy state, and queue refresh.
 */
export function useReviewDecision() {
  const [submitting, setSubmitting] = useState(false);
  const fetchReviewQueue = useDocumentCaptureStore((s) => s.fetchReviewQueue);
  const settleReviewedRow = useDocumentCaptureStore((s) => s.settleReviewedRow);

  async function submit(
    run: () => Promise<void>,
    opts: {
      successMessage: string;
      onSuccess?: () => void;
      /**
       * 86d3x48q7 (T13) — pass the scan this decision identified, and its live-stream row walks
       * through the linking step ("you confirmed it · writing the link into NetSuite…") instead of
       * silently flipping to `linked` when the server's event lands. Omit for decisions that do not
       * identify a scan (keep-watching, reject, spot-check confirm — nothing new gets linked).
       */
      settle?: { fileId: number | null; docNumber: string | null };
    }
  ): Promise<boolean> {
    setSubmitting(true);
    try {
      await run();
      toast.success(opts.successMessage);
      // Settle BEFORE the refetch: the row transition is the user's own feedback and should land
      // immediately, not after a round trip they did not ask for.
      if (opts.settle) settleReviewedRow(opts.settle);
      await fetchReviewQueue();
      opts.onSuccess?.();
      return true;
    } catch (err) {
      const status = err instanceof AxiosError ? err.response?.status : undefined;
      const serverMessage =
        err instanceof AxiosError
          ? (err.response?.data as { message?: string } | undefined)?.message
          : undefined;

      if (status === 403) {
        // Global interceptor already toasted. Refresh so any permission-driven change is reflected.
        await fetchReviewQueue();
      } else if (status === 409) {
        toast.warning('This changed while you were looking at it', {
          description: 'The queue was refreshed — take another look before deciding.',
        });
        await fetchReviewQueue();
      } else {
        toast.error(serverMessage ?? 'That action didn’t go through. Please try again.');
      }
      return false;
    } finally {
      setSubmitting(false);
    }
  }

  return { submit, submitting };
}
